import type { ResourceKey } from 'i18next'

import authEnUS from '@/pages/auth/locales/auth.en-US.json'
import authEs from '@/pages/auth/locales/auth.es.json'
import authPtBR from '@/pages/auth/locales/auth.pt-BR.json'

import commonEnUS from '../locales/common.en-US.json'
import commonEs from '../locales/common.es.json'
import commonPtBR from '../locales/common.pt-BR.json'
import componentsEnUS from '../locales/components.en-US.json'
import componentsEs from '../locales/components.es.json'
import componentsPtBR from '../locales/components.pt-BR.json'

export const coreResources = {
  'pt-BR': {
    common: commonPtBR,
    components: componentsPtBR,
    auth: authPtBR,
  },
  'en-US': {
    common: commonEnUS,
    components: componentsEnUS,
    auth: authEnUS,
  },
  es: {
    common: commonEs,
    components: componentsEs,
    auth: authEs,
  },
}

type LocaleModule = { default: ResourceKey }

const localeModules = import.meta.glob<LocaleModule>('/src/**/locales/*.json')

const FILE_NAME_PATTERN = /^(?<namespace>[^.]+)\.(?<language>[^.]+)\.json$/

function buildLoaderMap() {
  const loaders = new Map<string, () => Promise<LocaleModule>>()

  for (const [filePath, load] of Object.entries(localeModules)) {
    const fileName = filePath.split('/').at(-1) ?? ''
    const groups = FILE_NAME_PATTERN.exec(fileName)?.groups

    if (!groups) {
      throw new Error(
        `[i18n] "${filePath}" does not follow the convention <namespace>.<language>.json`,
      )
    }

    const id = resourceId(groups.language, groups.namespace)

    if (loaders.has(id)) {
      throw new Error(
        `[i18n] duplicate namespace: "${fileName}" exists in more than one locales/ folder`,
      )
    }

    loaders.set(id, load)
  }

  return loaders
}

function resourceId(language: string, namespace: string) {
  return `${language}|${namespace}`
}

const loaderMap = buildLoaderMap()

export async function loadNamespaceResource(
  language: string,
  namespace: string,
): Promise<ResourceKey | null> {
  const load = loaderMap.get(resourceId(language, namespace))

  if (!load) {
    return null
  }

  const resource = await load()

  return resource.default
}
